import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

interface CategoryAnalyticsData {
  category: string;
  totalRevenue: number;
  redemptionCount: number;
  avgRevenuePerRedemption: number;
  color: string;
}

interface CategoryBreakdownProps {
  // Remove dependency on external categories prop
}

type TabType = 'totalRevenue' | 'redemptionCount' | 'avgRevenue';

const CategoryBreakdown = ({}: CategoryBreakdownProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('totalRevenue');
  const [categoryData, setCategoryData] = useState<CategoryAnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load category performance data from Supabase
  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get redemption data with offer details to calculate category performance
      const { data: redemptionData, error: redemptionError } = await supabase
        .from('redemptions')
        .select(`
          final_price,
          saved_amount,
          offers!inner(
            category
          )
        `);

      if (redemptionError) throw redemptionError;

      // Process data by category
      const categoryMap = new Map<string, {
        totalRevenue: number;
        redemptionCount: number;
      }>();

      // Aggregate redemption data by category
      redemptionData?.forEach(redemption => {
        const category = redemption.offers?.category || 'Other';
        const existing = categoryMap.get(category) || { totalRevenue: 0, redemptionCount: 0 };
        
        existing.totalRevenue += redemption.final_price || 0;
        existing.redemptionCount += 1;
        
        categoryMap.set(category, existing);
      });

      // Convert to array and calculate average revenue per redemption
      const colors = ['#178EEF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
      let colorIndex = 0;

      const processedData: CategoryAnalyticsData[] = Array.from(categoryMap.entries()).map(([category, data]) => ({
        category,
        totalRevenue: data.totalRevenue,
        redemptionCount: data.redemptionCount,
        avgRevenuePerRedemption: data.redemptionCount > 0 ? data.totalRevenue / data.redemptionCount : 0,
        color: colors[colorIndex++ % colors.length]
      }));

      // Set the data without initial sorting - we'll sort based on active tab
      setCategoryData(processedData);
    } catch (error: any) {
      console.error('Error loading category data:', error);
      setError(error.message || 'Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategoryData();
  }, []);

  // Memoized sorted category data based on active tab
  const sortedCategoryData = useMemo(() => {
    if (!categoryData.length) return [];

    const sorted = [...categoryData];
    
    switch (activeTab) {
      case 'totalRevenue':
        return sorted.sort((a, b) => b.totalRevenue - a.totalRevenue);
      case 'redemptionCount':
        return sorted.sort((a, b) => b.redemptionCount - a.redemptionCount);
      case 'avgRevenue':
        return sorted.sort((a, b) => b.avgRevenuePerRedemption - a.avgRevenuePerRedemption);
      default:
        return sorted;
    }
  }, [categoryData, activeTab]);

  // Get data for current tab using sorted data
  const getChartData = () => {
    switch (activeTab) {
      case 'totalRevenue':
        return sortedCategoryData.map(item => ({
          name: item.category,
          value: item.totalRevenue,
          color: item.color
        }));
      case 'redemptionCount':
        return sortedCategoryData.map(item => ({
          name: item.category,
          value: item.redemptionCount,
          color: item.color
        }));
      case 'avgRevenue':
        return sortedCategoryData.map(item => ({
          name: item.category,
          value: item.avgRevenuePerRedemption,
          color: item.color
        }));
      default:
        return [];
    }
  };

  // Get tab title and description
  const getTabInfo = () => {
    switch (activeTab) {
      case 'totalRevenue':
        return {
          title: 'Total Revenue',
          description: 'Revenue generated per category',
          format: (value: number) => `€${value.toLocaleString()}`
        };
      case 'redemptionCount':
        return {
          title: 'Redemption Count',
          description: 'Number of redemptions per category',
          format: (value: number) => value.toLocaleString()
        };
      case 'avgRevenue':
        return {
          title: 'Avg Revenue per Redemption',
          description: 'Average revenue generated per redemption by category',
          format: (value: number) => `€${value.toFixed(2)}`
        };
      default:
        return {
          title: 'Category Performance',
          description: 'Category analytics',
          format: (value: number) => value.toString()
        };
    }
  };

  const tabInfo = getTabInfo();
  const chartData = getChartData();
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-muted-foreground">Loading category performance...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-lg text-destructive mb-2">Error loading data</div>
            <div className="text-sm text-muted-foreground">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Category Performance</h3>
          <p className="text-sm text-muted-foreground">Revenue breakdown and redemption analytics</p>
        </div>
        <Icon name="PieChart" size={20} className="text-muted-foreground" />
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('totalRevenue')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'totalRevenue' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Total Revenue
        </button>
        <button
          onClick={() => setActiveTab('redemptionCount')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'redemptionCount' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Redemption Count
        </button>
        <button
          onClick={() => setActiveTab('avgRevenue')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'avgRevenue' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Avg Revenue per Redemption
        </button>
      </div>

      {/* Current Tab Title */}
      <div className="mb-4">
        <h4 className="text-base font-semibold text-foreground">{tabInfo.title}</h4>
        <p className="text-sm text-muted-foreground">{tabInfo.description}</p>
      </div>

      {/* Show message if no data */}
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
            <div className="text-lg text-muted-foreground mb-2">No data available</div>
            <div className="text-sm text-muted-foreground">Category performance will appear when redemption data is available</div>
          </div>
        </div>
      ) : (
        <>
          {/* Pie Chart */}
          <div className="h-64 mb-6" aria-label={`${tabInfo.title} Pie Chart`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [tabInfo.format(value), tabInfo.title]}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Category List - Now uses sortedCategoryData for dynamic ranking */}
          <div className="space-y-4">
            {sortedCategoryData.map((category, index) => {
              const currentValue = chartData.find(item => item.name === category.category)?.value || 0;
              const percentage = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
              
              return (
                <div key={category.category} className="flex items-center justify-between p-3 bg-muted rounded-lg transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {/* Ranking number */}
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      ></div>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{category.category}</p>
                      <p className="text-sm text-muted-foreground">
                        {percentage.toFixed(1)}% of total
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-foreground">
                      {tabInfo.format(currentValue)}
                    </p>
                    <div className="text-xs text-muted-foreground">
                      <p>€{category.totalRevenue.toLocaleString()} revenue</p>
                      <p>{category.redemptionCount} redemptions</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Total {tabInfo.title}
              </span>
              <span className="text-lg font-bold text-foreground">
                {tabInfo.format(totalValue)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryBreakdown;